// Mantis #1371 (owner ruling 2026-09-04, verbatim): "The orgnal
// design where we set RA from the web interface is gone. RA is a protected
// wall that sertive [serves] password to the newwork [network]. We only allow
// through that wall group access for only the trusted people. Hacker most not
// be able to get access via the Core/WebUI." The door-discovery/onboarding
// surface (MAD001-F0081..F0084) is removed for exactly that reason — see
// docs/tests-Web/T-MAD001-0081-0084-RealmAuthority-Door-Web-Test-Plan.md
// (struck) — and this page never regrows it.
//
// Mantis #1453 update: this shell no longer reads the generic multi-app
// serviceLinksApi context for its own "RealmAuthority link" card — that
// surface modelled MULTIPLE installations per organisation, which the lead's
// cardinality ruling (one RealmAuthority realm per organisation, Mantis
// #1453) retires entirely. The real, contract-backed link/groups/directory
// state (interfaces/core-realmauthority-editor/v1, SAPI-D029-004) is read
// and rendered by RealmAuthorityGroupsEditor itself, including the honest
// `linked:false` state — never a guess, never duplicated up here. This shell
// keeps only the organisation-scope resolution (admin vs super_admin, shared
// with the access screens) and the honest "no group listing is available
// yet" line for the one state that is genuinely this shell's own: no
// organisation in scope yet.
import React, { useContext, useEffect, useState } from 'react';
import Layout from '../layout/Layout';
import AuthContext from '../../context/AuthContext';
import { organisationApi } from '../../services/api';
import {
  isGlobalSuperAdmin,
  resolveAdminRoomsSelection,
  selectAdminRoomsOrganisations,
} from './adminRoomsOrganisationScope';
import RealmAuthorityGroupsEditor from './realmAuthority/RealmAuthorityGroupsEditor';
import './RealmAuthorityGroupsPage.css';

export default function RealmAuthorityGroupsPage() {
  const { user } = useContext(AuthContext) || {};
  // Mantis #1378/#1453: the groups editor is mounted per organisation. The
  // org scope resolves through the SAME shared, tested helpers as the access
  // screens (adminRoomsOrganisationScope.js): a plain admin gets their
  // administered organisation; a super_admin gets an explicit chooser and
  // no listing until one is chosen — never a guess across tenants.
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrganisationId, setSelectedOrganisationId] = useState('');
  const [scopeReady, setScopeReady] = useState(false);
  const authScopeSubject = user?.uuid ?? user?.id ?? user?.email ?? '';
  const canResolveScope = Boolean(authScopeSubject)
    && (user?.role === 'admin' || user?.role === 'super_admin');
  const scopeUser = { role: user?.role };
  const authScopeIdentity = [authScopeSubject || 'anonymous', user?.role ?? 'none'].map(String).join(':');

  useEffect(() => {
    let active = true;
    setScopeReady(false);
    if (!canResolveScope) {
      setOrganisations([]);
      setSelectedOrganisationId('');
      setScopeReady(true);
      return undefined;
    }
    organisationApi.getMine().then((response) => {
      if (!active) return;
      const orgsData = response.data?.data || [];
      const nextOrganisations = selectAdminRoomsOrganisations(orgsData, scopeUser);
      setOrganisations(nextOrganisations);
      setSelectedOrganisationId(resolveAdminRoomsSelection('', nextOrganisations, scopeUser));
      setScopeReady(true);
    }).catch(() => {
      // Honest failure: without a resolved scope there is no listing to
      // show, so the "No group listing is available yet." state stays.
      if (!active) return;
      setOrganisations([]);
      setSelectedOrganisationId('');
      setScopeReady(true);
    });
    return () => { active = false; };
    // The effect intentionally keys on the scope identity, not the user object;
    // this repo does not configure the exhaustive-deps lint rule (see
    // MediaLibraryGrid.js) — a plain comment, not a disable directive.
  }, [authScopeIdentity, canResolveScope]);

  const editorMounted = scopeReady && Boolean(selectedOrganisationId);

  return (
    <Layout>
      <section className="ra-groups-page" data-testid="ra-groups-page" aria-labelledby="ra-groups-title">
        <header className="ra-groups-header">
          <p className="ra-groups-eyebrow">RealmAuthority</p>
          <h1 id="ra-groups-title">Access Authority</h1>
          <p>
            RealmAuthority is a protected wall between the trusted network and the
            people allowed to pass through it. RealmAuthority publishes its groups
            and their members here for the organisation&apos;s administrators; below
            you can request membership changes, which RealmAuthority itself
            validates and may refuse. This web interface has no
            door-discovery, onboarding, or RealmAuthority-configuration capability.
          </p>
        </header>

        {scopeReady && organisations.length > 1 ? (
          <select
            className="ra-groups-org-filter"
            value={selectedOrganisationId}
            onChange={(event) => setSelectedOrganisationId(resolveAdminRoomsSelection(event.target.value, organisations, scopeUser))}
            aria-label="Organisation"
            data-testid="ra-groups-organisation-filter"
          >
            {isGlobalSuperAdmin(scopeUser) ? <option value="">Choose an organisation…</option> : null}
            {organisations.map((organisation) => (
              <option key={organisation.id} value={organisation.id}>{organisation.name}</option>
            ))}
          </select>
        ) : null}

        {editorMounted ? (
          // Mantis #1378/#1453 — the Access Authority listing + membership
          // editor. While it is mounted it owns the WHOLE link/groups/
          // directory listing area, including the honest "no group listing
          // is available yet" state (absent API, 401, 403 — never a
          // fabricated row), so this shell renders its own ra-groups-empty
          // line only when no organisation is in scope at all.
          <RealmAuthorityGroupsEditor key={selectedOrganisationId} organisationId={selectedOrganisationId} />
        ) : null}

        {scopeReady && !editorMounted ? (
          // True only with no organisation in scope — never conditioned on
          // Core's response, which the editor above owns entirely once mounted.
          <p className="ra-groups-empty" data-testid="ra-groups-empty">No group listing is available yet.</p>
        ) : null}
      </section>
    </Layout>
  );
}
